import type { MonthlyRecord, RawRow } from '../hooks/useCurrencyDashboard';

/** Standard JSON columns vs parallel `*_ex_tax_ex_deposit` revenue columns. */
export type RevenueAmountMode = 'with_tax_deposit' | 'ex_tax_ex_deposit';

const EX_TAX_DEP = '_ex_tax_ex_deposit';

function readNumericField(row: Record<string, unknown>, keys: string[]): number {
  for (const k of keys) {
    const v = row[k];
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
}

function readOptionalNumericField(row: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = row[k];
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

/** Amount fields: strings or numbers in JSON; ex-tax-ex-deposit mode prefers `*_ex_tax_ex_deposit` then falls back. */
export function rowAmount(row: RawRow, stdField: string, mode: RevenueAmountMode): number {
  if (mode === 'ex_tax_ex_deposit') {
    return readNumericField(row, [`${stdField}${EX_TAX_DEP}`, stdField]);
  }
  return readNumericField(row, [stdField]);
}

function rowOptionalAmount(row: RawRow, stdField: string, mode: RevenueAmountMode): number | null {
  if (mode === 'ex_tax_ex_deposit') {
    return readOptionalNumericField(row, [`${stdField}${EX_TAX_DEP}`, stdField]);
  }
  return readOptionalNumericField(row, [stdField]);
}

function rowOptionalPct(row: RawRow, stdField: string, mode: RevenueAmountMode): number | null {
  return rowOptionalAmount(row, stdField, mode);
}

// ─── Color palette ────────────────────────────────────────────────────────────
export const STACK_COLORS = [
  '#2563EB',
  '#059669',
  '#D97706',
  '#7C3AED',
  '#0891B2',
  '#E11D48',
];

// ─── Types ────────────────────────────────────────────────────────────────────
export interface CurrencyEntry {
  currency: string;
  usdAmount: number;
  originalAmount: number;
  pctShare: number;
}

export interface StackChartRow {
  month: string;
  ym: number;
  stackGrandUsd: number;
  [ccy: string]: number | string;
}

export interface SingleCcyRow {
  month: string;
  ym: number;
  amount: number;
  usd: number;
  momPct: number | null;
  yoyPct: number | null;
  sharePct: number | null;
}

export interface PivotTrendRow {
  month: string;
  ym: number;
  [key: string]: number | string | null;
}

export interface AllTableRow {
  month: string;
  ym: number;
  cols: Record<string, { amount: number; momPct: number | null }>;
  grandUsd: number;
}

export interface SingleCcyTableRow {
  month: string;
  ym: number;
  local: number;
  usd: number;
  sharePct: number | null;
  momUsdPct: number | null;
  momLocalPct: number | null;
  yoyPct: number | null;
}

export interface KPIResult {
  totalUsd: number;
  avgPerMonthUsd: number;
  momPct: number | null;
  momUsd: number | null;
  yoyPct: number | null;
  yoyUsd: number | null;
  threeMonthAvg: number;
  invoiceCount: number;
  uniqueClients: number;
  currenciesUsed: number;
}

export interface CompareResult {
  current: { label: string; totalUsd: number; byCurrency: CurrencyEntry[] };
  prior: { label: string; totalUsd: number; byCurrency: CurrencyEntry[] };
}

export interface ContribSnapshotCard {
  ccy: string;
  share: number;
  momRev: number | null;
  momLocal: number | null;
  revenueUp: boolean;
  revenueDown: boolean;
}

// ─── Formatters ───────────────────────────────────────────────────────────────
export function getCurrencySymbol(ccy: string): string {
  const map: Record<string, string> = {
    USD: '$',
    GBP: '£',
    EUR: '€',
    INR: '₹',
    ZAR: 'R',
    CAD: 'C$',
    AUD: 'A$',
    JPY: '¥',
  };
  return map[ccy] ?? ccy;
}

export function fmtUsd(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

export function fmtCompact(n: number, sym?: string): string {
  const s = sym ?? '$';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${s}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${s}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${s}${Math.round(abs).toLocaleString('en-US')}`;
}

export function fmtPct(v: number | null | undefined, places = 1): string {
  if (v == null || isNaN(v as number)) return '—';
  const sign = (v as number) >= 0 ? '+' : '';
  return `${sign}${(v as number).toFixed(places)}%`;
}

export function prevInvoiceMonthKey(ym: number): number {
  const year = Math.floor(ym / 100);
  const month = ym % 100;
  if (month === 1) return (year - 1) * 100 + 12;
  return year * 100 + (month - 1);
}

export function monthLabelFromKey(ym: number): string {
  const year = Math.floor(ym / 100);
  const month = ym % 100;
  const date = new Date(year, month - 1, 1);
  const mon = date.toLocaleString('en-US', { month: 'short' });
  const yr = String(year).slice(2);
  return `${mon} '${yr}`;
}

export function entriesFromMonthlyRecord(
  item: MonthlyRecord,
  mode: RevenueAmountMode = 'with_tax_deposit'
): CurrencyEntry[] {
  const totalUsd = rowAmount(item, 'monthly_grand_total_usd', mode);
  return item._currencyRows.map(row => {
    const usd = rowAmount(row, 'currency_revenue_usd', mode);
    return {
      currency: row.currency_code,
      usdAmount: usd,
      originalAmount: rowAmount(row, 'currency_revenue_original', mode),
      pctShare: totalUsd > 0 ? (usd / totalUsd) * 100 : 0,
    };
  });
}

// ─── Filters ──────────────────────────────────────────────────────────────────
export function filterMonthlyData(
  data: MonthlyRecord[],
  fy: string | string[],   // 'all' | single FY string | array of FY strings (empty = all)
  quarter: string,
  month: string
): MonthlyRecord[] {
  return data.filter(item => {
    // Multi-year support: array means filter to any of the selected FYs
    if (Array.isArray(fy)) {
      if (fy.length > 0 && !fy.includes(item.financial_year_period)) return false;
    } else {
      if (fy !== 'all' && item.financial_year_period !== fy) return false;
    }
    if (quarter !== 'all') {
      const q = parseInt(quarter.replace('q', ''), 10);
      if (item.financial_quarter !== q) return false;
    }
    if (month !== 'all') {
      const m = parseInt(month, 10);
      // Full yyyymm key (>= 100000) → match invoice_month_key exactly (year-precise)
      // Legacy month-only value (1–12) → match calendar_month for backwards compat
      if (m >= 100000) {
        if (item.invoice_month_key !== m) return false;
      } else {
        if (item.calendar_month !== m) return false;
      }
    }
    return true;
  });
}

// ─── KPIs ─────────────────────────────────────────────────────────────────────
export function computeKPIs(
  filtered: MonthlyRecord[],
  _all: MonthlyRecord[],
  _fy: string,
  _quarter: string,
  _month: string,
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): KPIResult {
  if (filtered.length === 0) {
    return {
      totalUsd: 0,
      avgPerMonthUsd: 0,
      momPct: null,
      momUsd: null,
      yoyPct: null,
      yoyUsd: null,
      threeMonthAvg: 0,
      invoiceCount: 0,
      uniqueClients: 0,
      currenciesUsed: 0,
    };
  }

  const totalUsd = filtered.reduce((s, r) => s + rowAmount(r, 'global_invoiced_usd', revenueMode), 0);
  const avgPerMonthUsd = totalUsd / filtered.length;

  // For MoM and YoY, use the latest month in filtered
  const sorted = [...filtered].sort((a, b) => b.invoice_month_key - a.invoice_month_key);
  const latest = sorted[0];

  const momPct = rowOptionalPct(latest, 'global_mom_change_percent', revenueMode);
  const momUsd = rowOptionalAmount(latest, 'global_mom_change_usd', revenueMode);
  const yoyPct = rowOptionalPct(latest, 'global_yoy_change_percent', revenueMode);
  const yoyUsd = rowOptionalAmount(latest, 'global_yoy_change_usd', revenueMode);
  const threeMonthAvg = rowAmount(latest, 'global_three_month_moving_avg_usd', revenueMode);

  const invoiceCount = filtered.reduce((s, r) => s + (r.global_invoice_count || 0), 0);
  const uniqueClients = Math.max(...filtered.map(r => r.global_unique_clients || 0));
  const currenciesUsed = Math.max(...filtered.map(r => r.distinct_currencies_in_month || 0));

  return {
    totalUsd,
    avgPerMonthUsd,
    momPct,
    momUsd,
    yoyPct,
    yoyUsd,
    threeMonthAvg,
    invoiceCount,
    uniqueClients,
    currenciesUsed,
  };
}

// ─── Currency breakdown ───────────────────────────────────────────────────────
export function aggregateCurrencyBreakdown(
  filtered: MonthlyRecord[],
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): CurrencyEntry[] {
  const map = new Map<string, { usdAmount: number; originalAmount: number }>();

  for (const item of filtered) {
    for (const row of item._currencyRows) {
      const ccy = row.currency_code;
      const usd = rowAmount(row, 'currency_revenue_usd', revenueMode);
      const orig = rowAmount(row, 'currency_revenue_original', revenueMode);
      if (!map.has(ccy)) map.set(ccy, { usdAmount: 0, originalAmount: 0 });
      const existing = map.get(ccy)!;
      existing.usdAmount += usd;
      existing.originalAmount += orig;
    }
  }

  const totalUsd = Array.from(map.values()).reduce((s, v) => s + v.usdAmount, 0);
  const entries = Array.from(map.entries()).map(([currency, v]) => ({
    currency,
    usdAmount: v.usdAmount,
    originalAmount: v.originalAmount,
    pctShare: totalUsd > 0 ? (v.usdAmount / totalUsd) * 100 : 0,
  }));

  return entries.sort((a, b) => b.usdAmount - a.usdAmount);
}

// ─── Stack chart ──────────────────────────────────────────────────────────────
export function buildStackChartData(
  filtered: MonthlyRecord[],
  _all: MonthlyRecord[],
  topCcys: string[],
  denom: 'usd' | 'native' = 'usd',
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): StackChartRow[] {
  const sorted = [...filtered].sort((a, b) => a.invoice_month_key - b.invoice_month_key);
  return sorted.map(item => {
    const row: StackChartRow = {
      month: monthLabelFromKey(item.invoice_month_key),
      ym: item.invoice_month_key,
      stackGrandUsd: rowAmount(item, 'global_invoiced_usd', revenueMode),
    };
    for (const ccy of topCcys) {
      const ccyRow = item._currencyRows.find(r => r.currency_code === ccy);
      row[ccy] = ccyRow
        ? rowAmount(
            ccyRow,
            denom === 'native' ? 'currency_revenue_original' : 'currency_revenue_usd',
            revenueMode
          )
        : 0;
    }
    return row;
  });
}

// ─── Single ccy chart ─────────────────────────────────────────────────────────
export function buildSingleCcyChartData(
  filtered: MonthlyRecord[],
  _all: MonthlyRecord[],
  ccy: string,
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): SingleCcyRow[] {
  const sorted = [...filtered].sort((a, b) => a.invoice_month_key - b.invoice_month_key);
  return sorted.flatMap(item => {
    const ccyRow = item._currencyRows.find(r => r.currency_code === ccy);
    if (!ccyRow) return [];
    return [{
      month: monthLabelFromKey(item.invoice_month_key),
      ym: item.invoice_month_key,
      amount: rowAmount(ccyRow, 'currency_revenue_original', revenueMode),
      usd: rowAmount(ccyRow, 'currency_revenue_usd', revenueMode),
      momPct: rowOptionalPct(ccyRow, 'mom_currency_revenue_change_pct', revenueMode),
      yoyPct: rowOptionalPct(ccyRow, 'yoy_currency_revenue_change_pct', revenueMode),
      sharePct: rowOptionalPct(ccyRow, 'pct_contribution_to_month_total', revenueMode),
    }];
  });
}

// ─── Pivot trend rows ─────────────────────────────────────────────────────────
export function buildPivotTrendRows(
  rawSlice: RawRow[],
  pivotCurrencies: string[],
  _allData: RawRow[],
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): PivotTrendRow[] {
  const monthKeys = Array.from(new Set(rawSlice.map(r => r.invoice_month_key))).sort();

  return monthKeys.map(ym => {
    const monthRows = rawSlice.filter(r => r.invoice_month_key === ym);
    const totalUsd = monthRows.reduce((s, r) => s + rowAmount(r, 'currency_revenue_usd', revenueMode), 0);
    const row: PivotTrendRow = {
      month: monthLabelFromKey(ym),
      ym,
    };

    for (const ccy of pivotCurrencies) {
      const ccyRow = monthRows.find(r => r.currency_code === ccy);
      const usd = ccyRow ? rowAmount(ccyRow, 'currency_revenue_usd', revenueMode) : 0;
      const local = ccyRow ? rowAmount(ccyRow, 'currency_revenue_original', revenueMode) : 0;
      const share = totalUsd > 0 ? (usd / totalUsd) * 100 : 0;
      row[`revUsd_${ccy}`] = usd;
      row[`revLocal_${ccy}`] = local;
      row[`share_${ccy}`] = share;
      row[`momPct_${ccy}`] = ccyRow ? rowOptionalPct(ccyRow, 'mom_currency_revenue_change_pct', revenueMode) : null;
      row[`momLocalPct_${ccy}`] = ccyRow ? rowOptionalPct(ccyRow, 'mom_currency_revenue_change_pct', revenueMode) : null;
    }
    return row;
  });
}

// ─── All table rows ───────────────────────────────────────────────────────────
export function buildAllTableRows(
  filtered: MonthlyRecord[],
  _all: MonthlyRecord[],
  topCcys: string[],
  denom: 'usd' | 'native',
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): AllTableRow[] {
  const sorted = [...filtered].sort((a, b) => b.invoice_month_key - a.invoice_month_key);
  return sorted.map(item => {
    const cols: Record<string, { amount: number; momPct: number | null }> = {};
    for (const ccy of topCcys) {
      const ccyRow = item._currencyRows.find(r => r.currency_code === ccy);
      cols[ccy] = {
        amount: ccyRow
          ? rowAmount(
              ccyRow,
              denom === 'native' ? 'currency_revenue_original' : 'currency_revenue_usd',
              revenueMode
            )
          : 0,
        momPct: ccyRow ? rowOptionalPct(ccyRow, 'mom_currency_revenue_change_pct', revenueMode) : null,
      };
    }
    return {
      month: monthLabelFromKey(item.invoice_month_key),
      ym: item.invoice_month_key,
      cols,
      grandUsd: rowAmount(item, 'global_invoiced_usd', revenueMode),
    };
  });
}

// ─── Single ccy table rows ────────────────────────────────────────────────────
export function buildSingleCcyTableRows(
  filteredRaw: RawRow[],
  _allRaw: RawRow[],
  ccy: string,
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): SingleCcyTableRow[] {
  const rows = filteredRaw.filter(r => r.currency_code === ccy);
  const sorted = [...rows].sort((a, b) => b.invoice_month_key - a.invoice_month_key);

  return sorted.map(r => {
    const totalMonthUsd = rowAmount(r, 'monthly_grand_total_usd', revenueMode);
    const usd = rowAmount(r, 'currency_revenue_usd', revenueMode);
    return {
      month: monthLabelFromKey(r.invoice_month_key),
      ym: r.invoice_month_key,
      local: rowAmount(r, 'currency_revenue_original', revenueMode),
      usd,
      sharePct: totalMonthUsd > 0 ? (usd / totalMonthUsd) * 100 : null,
      momUsdPct: rowOptionalPct(r, 'mom_currency_revenue_change_pct', revenueMode),
      momLocalPct: rowOptionalPct(r, 'mom_currency_revenue_change_pct', revenueMode),
      yoyPct: rowOptionalPct(r, 'yoy_currency_revenue_change_pct', revenueMode),
    };
  });
}

// ─── Compare data ─────────────────────────────────────────────────────────────
function sumByCurrency(items: MonthlyRecord[], revenueMode: RevenueAmountMode): CurrencyEntry[] {
  const map = new Map<string, number>();
  let total = 0;
  for (const item of items) {
    for (const row of item._currencyRows) {
      const usd = rowAmount(row, 'currency_revenue_usd', revenueMode);
      map.set(row.currency_code, (map.get(row.currency_code) || 0) + usd);
      total += usd;
    }
  }
  return Array.from(map.entries())
    .map(([currency, usdAmount]) => ({
      currency,
      usdAmount,
      originalAmount: 0,
      pctShare: total > 0 ? (usdAmount / total) * 100 : 0,
    }))
    .sort((a, b) => b.usdAmount - a.usdAmount);
}

export function buildCompareData(
  allData: MonthlyRecord[],
  fy: string | string[],
  quarter: string,
  month: string,
  compareKind: 'mom' | 'yoy_month' | 'qoq' | 'h1' | 'h2' | 'yoy',
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): CompareResult {
  const current = filterMonthlyData(allData, fy, quarter, month);
  const sortedCurrent = [...current].sort((a, b) => b.invoice_month_key - a.invoice_month_key);

  let priorItems: MonthlyRecord[] = [];
  let currentLabel = 'Current Period';
  let priorLabel = 'Prior Period';

  if (compareKind === 'mom' && sortedCurrent.length > 0) {
    const latestYm = sortedCurrent[0].invoice_month_key;
    const prevYm = prevInvoiceMonthKey(latestYm);
    currentLabel = monthLabelFromKey(latestYm);
    priorLabel = monthLabelFromKey(prevYm);
    priorItems = allData.filter(r => r.invoice_month_key === prevYm);
  } else if (compareKind === 'yoy_month' && sortedCurrent.length > 0) {
    const latestYm = sortedCurrent[0].invoice_month_key;
    const latestYear = Math.floor(latestYm / 100);
    const latestMonth = latestYm % 100;
    const prevYm = (latestYear - 1) * 100 + latestMonth;
    currentLabel = monthLabelFromKey(latestYm);
    priorLabel = monthLabelFromKey(prevYm);
    priorItems = allData.filter(r => r.invoice_month_key === prevYm);
  } else if (compareKind === 'qoq') {
    const currentQ = quarter !== 'all' ? parseInt(quarter.replace('q', ''), 10) : null;
    if (currentQ !== null) {
      const prevQ = currentQ === 1 ? 4 : currentQ - 1;
      const prevFy = currentQ === 1 ? (!isFyAll(fy) ? shiftFyMulti(fy, -1) : fy) : fy;
      currentLabel = `Q${currentQ} ${formatFyLabel(fy)}`;
      priorLabel = `Q${prevQ} ${formatFyLabel(prevFy)}`;
      priorItems = filterMonthlyData(allData, prevFy, `q${prevQ}`, 'all');
    }
  } else if (compareKind === 'h1') {
    currentLabel = `H1 ${formatFyLabel(fy)}`;
    priorLabel = 'H1 Prior';
    priorItems = filterMonthlyData(allData, shiftFyMulti(fy, -1), 'all', 'all').filter(
      r => r.financial_quarter === 1 || r.financial_quarter === 2
    );
  } else if (compareKind === 'h2') {
    currentLabel = `H2 ${formatFyLabel(fy)}`;
    priorLabel = 'H2 Prior';
    priorItems = filterMonthlyData(allData, shiftFyMulti(fy, -1), 'all', 'all').filter(
      r => r.financial_quarter === 3 || r.financial_quarter === 4
    );
  } else if (compareKind === 'yoy') {
    currentLabel = !isFyAll(fy) ? formatFyLabel(fy) : 'Current FY';
    const prevFy = !isFyAll(fy) ? shiftFyMulti(fy, -1) : 'all';
    priorLabel = formatFyLabel(prevFy);
    priorItems = filterMonthlyData(allData, prevFy, 'all', 'all');
  }

  let currentSlice = current;
  if (compareKind === 'h1') {
    currentSlice = current.filter(r => r.financial_quarter === 1 || r.financial_quarter === 2);
  } else if (compareKind === 'h2') {
    currentSlice = current.filter(r => r.financial_quarter === 3 || r.financial_quarter === 4);
  }

  const currentTotalUsd = currentSlice.reduce((s, r) => s + rowAmount(r, 'global_invoiced_usd', revenueMode), 0);
  const priorTotalUsd = priorItems.reduce((s, r) => s + rowAmount(r, 'global_invoiced_usd', revenueMode), 0);

  return {
    current: { label: currentLabel, totalUsd: currentTotalUsd, byCurrency: sumByCurrency(currentSlice, revenueMode) },
    prior: { label: priorLabel, totalUsd: priorTotalUsd, byCurrency: sumByCurrency(priorItems, revenueMode) },
  };
}

function shiftFy(fy: string, delta: number): string {
  if (fy === 'all') return 'all';
  const parts = fy.split('-');
  if (parts.length !== 2) return fy;
  const start = parseInt(parts[0], 10) + delta;
  const end = parseInt(parts[1], 10) + delta;
  return `${start}-${String(end).padStart(2, '0')}`;
}

function formatFyLabel(fy: string | string[]): string {
  if (fy === 'all') return 'All FYs';
  if (Array.isArray(fy)) return fy.length === 0 ? 'All FYs' : fy.join(', ');
  return fy;
}

function isFyAll(fy: string | string[]): boolean {
  return fy === 'all' || (Array.isArray(fy) && fy.length === 0);
}

/** Shift each FY label by `delta` years (e.g. -1 for prior FY). */
function shiftFyMulti(fy: string | string[], delta: number): string | string[] {
  if (fy === 'all') return 'all';
  if (Array.isArray(fy)) {
    if (fy.length === 0) return 'all';
    return fy.map(f => shiftFy(f, delta));
  }
  return shiftFy(fy, delta);
}

// ─── Contrib snapshot cards ───────────────────────────────────────────────────
export function buildContribSnapshotCards(
  rawSlice: RawRow[],
  _allRaw: RawRow[],
  latestYm: number,
  revenueMode: RevenueAmountMode = 'with_tax_deposit'
): ContribSnapshotCard[] {
  const latestRows = rawSlice.filter(r => r.invoice_month_key === latestYm);
  return latestRows
    .sort((a, b) => a.currency_rank_in_month - b.currency_rank_in_month)
    .map(row => {
      const mom = rowOptionalPct(row, 'mom_currency_revenue_change_pct', revenueMode) ?? 0;
      return {
        ccy: row.currency_code,
        share: rowAmount(row, 'pct_contribution_to_month_total', revenueMode),
        momRev: rowOptionalPct(row, 'mom_currency_revenue_change_pct', revenueMode),
        momLocal: rowOptionalPct(row, 'mom_currency_revenue_change_pct', revenueMode),
        revenueUp: mom > 0,
        revenueDown: mom < 0,
      };
    });
}
